import React from 'react';

import { QueryOptions } from '@apollo/client';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import {
  InboxItemDocument,
  InboxItemQueryVariables,
  InboxItemsScrollableListDocument,
  InboxItemsScrollableListQueryVariables,
  VerificationRequestDocument,
  VerificationRequestPaymentInfoDocument,
  VerificationRequestPaymentInfoQueryVariables,
  VerificationRequestQueryVariables,
} from 'src/api';
import { Modal, ModalTopAction } from 'src/components/common';
import { ViewVerificationRequestReportModalForm } from 'src/components/inboxItem';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemUnreadStatuses } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';

import { ViewVerificationRequestReportModalArgs } from './types';

export const ViewVerificationRequestReportModal: React.FC = () => {
  const { spawnNotification } = useNotification();
  const { closeModal, isOpen, args } = useModal<ViewVerificationRequestReportModalArgs>(
    ModalId.ViewVerificationRequestReportModal,
  );

  const { generatedReportsMeta, downloadGeneratedReportFilesArchive, archiveDownloading } =
    useGeneratedReports({
      inboxItemId: args?.inboxItemId,
      skip: !isOpen,
    });

  const { updateInboxItemUnreadStatuses } = useInboxItemUnreadStatuses(args?.inboxItemId);
  const { hasEmployerRoles } = useCurrentUserPermissions();
  const modalTitle = args?.verificationRequest?.type
    ? VERIFICATION_REQUEST_TYPE_LABEL[args.verificationRequest.type]
    : '';
  const { isMobile } = useScreenInfo();

  const handleCloseModal = React.useCallback(() => {
    closeModal();
    updateInboxItemUnreadStatuses();
  }, [closeModal, updateInboxItemUnreadStatuses]);

  const refetchQueriesOnAction = React.useMemo(
    (): QueryOptions[] => [
      {
        query: InboxItemsScrollableListDocument,
        variables: {
          where: { id: { equals: args?.inboxItemId || '' } },
        } satisfies InboxItemsScrollableListQueryVariables,
      },
      {
        query: InboxItemDocument,
        variables: { id: args?.inboxItemId || '' } satisfies InboxItemQueryVariables,
      },
      {
        query: VerificationRequestDocument,
        variables: {
          filter: { inboxItemId: args?.inboxItemId },
        } satisfies VerificationRequestQueryVariables,
      },
      {
        query: VerificationRequestPaymentInfoDocument,
        variables: {
          where: { inboxItemId: args?.inboxItemId },
        } satisfies VerificationRequestPaymentInfoQueryVariables,
      },
    ],
    [args?.inboxItemId],
  );

  const handleDownloadAllReports = React.useCallback(async () => {
    if (!generatedReportsMeta.length) {
      spawnNotification("There's no reports yet", 'error');
      return;
    }

    await downloadGeneratedReportFilesArchive(
      generatedReportsMeta.map(report => report.file.id),
      { refetchQueries: refetchQueriesOnAction },
    );
    await updateInboxItemUnreadStatuses();
  }, [
    generatedReportsMeta,
    downloadGeneratedReportFilesArchive,
    refetchQueriesOnAction,
    updateInboxItemUnreadStatuses,
    spawnNotification,
  ]);

  const mobileTopAction = React.useMemo((): ModalTopAction => {
    return {
      show: true,
      render: () => (
        <IconButton onClick={handleCloseModal} disabled={archiveDownloading}>
          <CloseIcon />
        </IconButton>
      ),
    };
  }, [handleCloseModal, archiveDownloading]);

  const topAction = React.useMemo((): ModalTopAction => {
    return {
      actionName: hasEmployerRoles ? 'Download' : 'Download All',
      show: true,
      icon: <DownloadIcon />,
      disabled: archiveDownloading,
      onAction: () => handleDownloadAllReports(),
    };
  }, [handleDownloadAllReports, hasEmployerRoles, archiveDownloading]);

  return (
    <Modal
      title={`${modalTitle} Report`}
      topAction={isMobile ? mobileTopAction : topAction}
      dialogProps={{
        fullWidth: true,
        fullScreen: isMobile,
        maxWidth: 'md',
        open: isOpen,
        onClose: handleCloseModal,
      }}
      titlePadding="32px 32px 16px 32px"
    >
      <ViewVerificationRequestReportModalForm
        inboxItemId={args?.inboxItemId}
        verificationRequest={args?.verificationRequest}
        handleDownloadAllReports={handleDownloadAllReports}
        onCloseModal={handleCloseModal}
        refetchQueriesOnAction={refetchQueriesOnAction}
      />
    </Modal>
  );
};
